// Puerta única de acceso al backend del módulo Calidad. Todos los componentes
// de src/pages/Calidad/ pasan por aquí — nunca hacer fetch directo desde un
// componente o desde el contexto.
//
// Partes, la relación Parte→Defecto y el registro múltiple (bulk) no existen
// todavía en el backend: se mockean por función (no por interceptor global),
// controlados por REACT_APP_CALIDAD_MOCK. Ver docs/backend-calidad.md para el
// contrato pedido al equipo de backend.
import {
    MOCK_PARTES,
    resolverParteDefectoPairs,
    inyectarCorridaEnCurso,
    mockDelay,
    esSerieDeError,
} from './calidadMocks';

const API = process.env.REACT_APP_SERVICE_ENDPOINT_NE;
const MOCK = process.env.REACT_APP_CALIDAD_MOCK === 'true';
const MOCK_LATENCY = Number(process.env.REACT_APP_CALIDAD_MOCK_LATENCY || 250);
const MOCK_CORRIDA_EN_CURSO = process.env.REACT_APP_CALIDAD_MOCK_CORRIDA_EN_CURSO || '';

const FETCH_TIMEOUT_MS = 15000;

export class CalidadApiError extends Error {
    constructor(message, status) {
        super(message);
        this.name = 'CalidadApiError';
        this.status = status;
    }
}

// ── Helpers internos ─────────────────────────────────────────────────────
function getToken() {
    try {
        const raw = sessionStorage.getItem('token');
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return parsed?.token || parsed;
    } catch {
        return null;
    }
}

// El formato de error del backend no es uniforme (Calidad-ContratoFront.md
// §1): 400 de validación propia, 401 y 404 llegan como texto plano; 400 de
// model binding y 500 llegan como JSON (ValidationProblemDetails /
// ProblemDetails). Si no es JSON válido, el cuerpo YA ES el mensaje —
// devolverlo tal cual en vez de descartarlo, para que el operario vea el
// detalle real ("No se encontró la corrida 12345 en la línea 3") en vez de
// un genérico.
export async function parseErrorMessage(res, fallback) {
    let text;
    try {
        text = await res.text();
    } catch {
        return fallback;
    }
    if (!text) return fallback;
    try {
        const json = JSON.parse(text);
        if (json.detail) return json.detail;
        if (json.title) return json.title;
        return fallback;
    } catch {
        return text.trim() || fallback;
    }
}

const ERROR_MESSAGES = {
    400: 'Los datos enviados no son válidos. Revisa los campos e intenta de nuevo.',
    401: 'Tu sesión ha expirado. Vuelve a iniciar sesión.',
    403: 'No tienes permiso para realizar esta acción.',
    404: 'No se encontró el registro solicitado.',
    409: 'Conflicto: el registro ya fue modificado por otro usuario.',
    500: 'Error interno del servidor. Intenta de nuevo más tarde.',
};

function getFriendlyError(status, serverDetail) {
    if (serverDetail && status !== 500) return serverDetail;
    return ERROR_MESSAGES[status] || 'Ocurrió un error inesperado. Intenta de nuevo.';
}

// Normaliza valores de formulario ('' | undefined | null) a null antes de
// mandarlos a campos int/int? de C#. System.Text.Json no bindea un string
// vacío contra un entero nullable y responde 400 — por eso, por ejemplo,
// registrar hoy sin causa raíz falla; esta función es la corrección.
export function toIntOrNull(v) {
    if (v === '' || v === null || v === undefined) return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
}

async function request(path, { method = 'GET', body, signal } = {}) {
    const token = getToken();
    if (!token) throw new CalidadApiError('Sin sesión activa. Vuelve a iniciar sesión.', 401);

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
    const onExternalAbort = () => controller.abort();
    if (signal) signal.addEventListener('abort', onExternalAbort);

    try {
        const res = await fetch(`${API}${path}`, {
            method,
            body,
            signal: controller.signal,
            headers: {
                authorization: `Bearer ${token}`,
                Accept: 'application/json',
                'Content-Type': 'application/json',
            },
        });
        if (!res.ok) {
            const fallback = ERROR_MESSAGES[res.status] || 'Ocurrió un error inesperado. Intenta de nuevo.';
            const detail = await parseErrorMessage(res, fallback);
            throw new CalidadApiError(getFriendlyError(res.status, detail), res.status);
        }
        if (res.status === 204) return null;
        const text = await res.text();
        return text ? JSON.parse(text) : null;
    } catch (err) {
        if (err instanceof CalidadApiError) throw err;
        if (err.name === 'AbortError') {
            throw new CalidadApiError('El servidor no respondió. Verifica tu conexión e intenta de nuevo.', 0);
        }
        throw new CalidadApiError(err.message || 'Error de red.', 0);
    } finally {
        clearTimeout(timeoutId);
        if (signal) signal.removeEventListener('abort', onExternalAbort);
    }
}

// ── Catálogos ─────────────────────────────────────────────────────────────
export async function getAreas(signal) {
    const data = await request('/areas', { signal });
    return Array.isArray(data) ? data : [];
}

export async function getLineas(area, signal) {
    const data = await request(`/lineas?area=${encodeURIComponent(area)}`, { signal });
    return Array.isArray(data) ? data : [];
}

export async function getCatalogoEnum(categoria, signal) {
    const data = await request(`/MicroServiciosController?categoria=${encodeURIComponent(categoria)}`, {
        signal,
    });
    return Array.isArray(data) ? data.map((d) => ({ codigo: d.codigo, label: d.nombre })) : [];
}

// Catálogo de partes — mock mientras no exista la categoría Enum PartesCalidad
// (ver docs/backend-calidad.md). Con el mock apagado se pide igual a la red
// real: si el backend aún no está desplegado, un 404 degrada a lista vacía y
// el módulo se comporta exactamente como hoy, sin selector de Parte.
export async function getPartes(signal) {
    if (MOCK) {
        await mockDelay(MOCK_LATENCY, signal);
        return MOCK_PARTES;
    }
    try {
        return await getCatalogoEnum('PartesCalidad', signal);
    } catch {
        return [];
    }
}

// Pares {parte, defecto} válidos, para filtrar el selector de defecto en
// cascada. Debe llamarse DESPUÉS de tener el catálogo real de defectos: el
// mock resuelve sus nombres contra los códigos reales de DefectosCalidad.
export async function getParteDefectoPairs(catalogoDefectos, signal) {
    if (MOCK) {
        await mockDelay(MOCK_LATENCY, signal);
        return resolverParteDefectoPairs(catalogoDefectos);
    }
    try {
        const data = await request('/Calidad/PartesDefectos', { signal });
        return Array.isArray(data) ? data : [];
    } catch {
        return [];
    }
}

// ── Corridas ──────────────────────────────────────────────────────────────
export async function getCorridas({ linea, fecha }, signal) {
    const medianocheLocal = new Date(fecha.getFullYear(), fecha.getMonth(), fecha.getDate());
    const data = await request(
        `/Corridas/GetCorridasByFecha?linea=${encodeURIComponent(linea)}&fecha=${encodeURIComponent(
            medianocheLocal.toISOString()
        )}`,
        { signal }
    );
    const corridas = (Array.isArray(data) ? data : []).map((c) => ({ ...c, enCurso: c.enCurso === true }));
    if (!MOCK) return corridas;
    return inyectarCorridaEnCurso(corridas, fecha, MOCK_CORRIDA_EN_CURSO);
}

// ── Registros ─────────────────────────────────────────────────────────────
export async function getRegistros(corrida, signal) {
    const data = await request(`/Calidad?corrida=${encodeURIComponent(corrida)}`, { signal });
    return Array.isArray(data) ? data : [];
}

// Crea un reporte de N líneas sobre la misma corrida/serie. `cantidad` no es
// un campo del contrato (Calidad-ContratoFront.md §7.1): el servidor la
// ignora si llega y siempre persiste 0, así que el front deja de mandarla.
// - Real: un solo POST /Calidad/Bulk transaccional.
// - Mock: fan-out SECUENCIAL contra el POST /Calidad que ya existe hoy — genera
//   filas auténticas en el engine de desarrollo en vez de folios inventados, y
//   la tabla se refresca con datos reales. No es transaccional (ese es
//   precisamente el argumento para pedir el endpoint real): si una línea
//   falla, las anteriores ya quedaron creadas. Por eso devuelve `fallidas`,
//   para que el llamador conserve solo lo que falta reintentar.
export async function crearReporte({ corrida, linea, loteoSerie, lineas }, signal) {
    if (!MOCK) {
        const res = await request('/Calidad/Bulk', {
            method: 'POST',
            signal,
            body: JSON.stringify({
                corrida,
                linea,
                loteoSerie: loteoSerie || null,
                lineas: lineas.map((l) => ({
                    parte: toIntOrNull(l.parte),
                    defecto: toIntOrNull(l.defecto),
                    causaRaiz: toIntOrNull(l.causaRaiz),
                    notas: l.notas || null,
                })),
            }),
        });
        return { folios: res?.folios || [], fallidas: [] };
    }

    await mockDelay(MOCK_LATENCY, signal);
    if (esSerieDeError(loteoSerie)) {
        throw new CalidadApiError('Simulación: el servidor rechazó el reporte.', 409);
    }

    const folios = [];
    const fallidas = [];
    for (let i = 0; i < lineas.length; i += 1) {
        try {
            const creado = await request('/Calidad', {
                method: 'POST',
                signal,
                body: JSON.stringify({
                    corrida,
                    linea,
                    parte: toIntOrNull(lineas[i].parte),
                    defecto: toIntOrNull(lineas[i].defecto),
                    causaRaiz: toIntOrNull(lineas[i].causaRaiz),
                    loteoSerie: loteoSerie || null,
                    notas: lineas[i].notas || null,
                }),
            });
            folios.push(creado?.folio);
        } catch (err) {
            fallidas.push({ indice: i, mensaje: err.message });
        }
    }
    return { folios, fallidas };
}

export async function actualizarRegistro(folio, cambios, signal) {
    await request(`/Calidad/${encodeURIComponent(folio)}`, {
        method: 'PUT',
        signal,
        body: JSON.stringify({
            parte: toIntOrNull(cambios.parte),
            defecto: toIntOrNull(cambios.defecto),
            causaRaiz: toIntOrNull(cambios.causaRaiz),
            loteoSerie: cambios.loteoSerie || null,
            notas: cambios.notas || null,
        }),
    });
}

export async function eliminarRegistro(folio, signal) {
    await request(`/Calidad/${encodeURIComponent(folio)}`, { method: 'DELETE', signal });
}
